/**
 * Adapters that reshape the WordPress `use_case` payload into the shapes the
 * shared blog-detail components expect.
 *
 * The `use_case` post type exposes a smaller REST payload than `posts`, so the
 * fields the blog detail UI reads have to be derived:
 *
 * | blog field        | use_case                       | derivation                       |
 * | ----------------- | ------------------------------ | -------------------------------- |
 * | `read_time`       | — (absent)                     | `estimateReadTime()` from content|
 * | `excerpt`/`meta`  | — (not registered in REST)     | `yoast_head_json.description`    |
 * | `related_posts`   | `related_use_cases`            | `mapRelatedUseCases()`           |
 * | `featured_image_url` | `blog_image`                | read directly                    |
 *
 * The listing payload is smaller still — see `toUseCaseListItem` below.
 */
import type {BlogListItem, UseCaseCategory, UseCaseListItem} from '@/types/listing';
import {decodeHtmlEntities} from '@/util/html-parser-utils';
import {resolveUseCaseAuthor} from '@/constants/useCaseAuthors';

/** WordPress ships unset custom fields as boolean `false`, not `''`. */
const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Average adult reading speed, words per minute. */
const WORDS_PER_MINUTE = 200;

/**
 * `use_case` has no backend-computed `read_time`, so estimate it from the rendered
 * content. Runs on the server (content is in the SSR payload), so the hero shows a
 * read time in the raw HTML rather than after hydration.
 */
export const estimateReadTime = (html: string): string => {
    const words = html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&[a-z]+;|&#\d+;/gi, ' ')
        .split(/\s+/)
        .filter(Boolean).length;
    if (!words) return '';
    return `${Math.max(1, Math.round(words / WORDS_PER_MINUTE))} min read`;
};

/**
 * `related_use_cases` entries carry an absolute WordPress `link` instead of a slug,
 * and no image. Pull the slug out of the URL so the card can link to our own route,
 * and drop anything unusable. Images fall back to the category pool / skeleton
 * inside `RelatedPosts`.
 */
export const mapRelatedUseCases = (relatedUseCases: any): any[] => {
    if (!Array.isArray(relatedUseCases)) return [];
    return relatedUseCases
        .map((item: any) => {
            if (!item?.id) return null;
            const slug = slugFromLink(item.link);
            if (!slug) return null;
            return {
                id: item.id,
                slug,
                title: asText(item.title),
                excerpt: asText(item.excerpt),
                date: asText(item.date)
            };
        })
        .filter(Boolean) as any[];
};

/** Last non-empty path segment of a WordPress permalink, e.g. `…/use_case/foo/` → `foo`. */
export const slugFromLink = (link: unknown): string => {
    const href = asText(link);
    if (!href) return '';
    try {
        return new URL(href).pathname.replace(/\/+$/, '').split('/').pop() ?? '';
    } catch {
        return href.replace(/\/+$/, '').split('/').pop() ?? '';
    }
};

/** Yoast's estimate, e.g. `{"Est. reading time": "43 minutes"}` → `43 min read`. */
const readTimeFromYoast = (misc?: Record<string, string>): string => {
    const minutes = Number.parseInt(misc?.['Est. reading time'] ?? '', 10);
    return Number.isNaN(minutes) ? '' : `${minutes} min read`;
};

/**
 * A post can sit in a broad and a narrow term at once (`AI` + `Generative AI`); the term with
 * the fewest posts is the most specific, so that one labels the card.
 */
const pickCategory = (ids: number[] | undefined, categories: UseCaseCategory[]): UseCaseCategory | undefined =>
    categories.filter((c) => ids?.includes(c.id)).sort((a, b) => a.count - b.count)[0];

/** A listing row: the shared card shape plus the slug its category label links to. */
export type UseCaseRowItem = BlogListItem & {categorySlug?: string};

/**
 * Reshapes a `use_case` listing row into the `BlogListItem` the shared listing cards read:
 *
 * | card field            | use_case listing source                    |
 * | --------------------- | ------------------------------------------ |
 * | `blog_image`          | `yoast_head_json.og_image[0].url`          |
 * | `meta.description`    | `yoast_head_json.description`              |
 * | `selected_categories` | `use_cases_categories` ids → term name     |
 * | `categorySlug`        | `use_cases_categories` ids → term slug     |
 * | `read_time`           | `yoast_head_json.twitter_misc`             |
 * | `author_info`         | all `use_cases_categories` → author map    |
 *
 * Title and description arrive HTML-encoded (`&amp;`, `&#8217;`) and are rendered as text,
 * so they are decoded here once rather than in every card.
 */
export const toUseCaseListItem = (item: UseCaseListItem, categories: UseCaseCategory[]): UseCaseRowItem => {
    const category = pickCategory(item.use_cases_categories, categories);
    return {
        id: item.id,
        slug: item.slug,
        date: item.date,
        modified: item.modified,
        title: {rendered: decodeHtmlEntities(item.title?.rendered ?? '')},
        blog_image: item.yoast_head_json?.og_image?.[0]?.url,
        meta: {description: decodeHtmlEntities(item.yoast_head_json?.description ?? '')},
        selected_categories: category ? [category.name] : [],
        categorySlug: category?.slug,
        read_time: readTimeFromYoast(item.yoast_head_json?.twitter_misc),
        author_info: resolveUseCaseAuthor(categories.filter((c) => item.use_cases_categories?.includes(c.id)).map((c) => c.slug))
    };
};
